// Форма замера InBody: сверка черновика из PDF, ручной ввод и правка сохранённого замера.
// Группы (R06): основное, состав, энергия и цели, нормы, сегменты; условия замера (R26).
// Предупреждения сверки — у своих полей; ошибка сервера с `field` подсвечивает поле,
// введённое остаётся (всё хранится строками до сохранения).

import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { ApiError } from '@/api/client'
import { Button, Card, ErrorText, Input, Select } from '@/components/ui'
import { pluralRu } from '@/lib/format'
import { toLocalInput } from '@/pages/metrics/series'

import { inbodyApi, NORM_KEYS, SEGMENTS, type ParseWarning, type ScanField, type WorkoutRelation } from './api'
import {
  errorAt,
  formPaths,
  fromForm,
  looseWarnings,
  matchesPath,
  SEGMENT_KINDS,
  warningsAt,
  type FormState,
  type ScanPayload,
} from './formState'
import { FIELD_GROUPS, FIELD_LABELS, NORM_LABELS, SEGMENT_KIND_LABELS, SEGMENT_LABELS } from './labels'

const FASTED_OPTIONS = [
  { value: '', label: 'Не помню' },
  { value: 'yes', label: 'Да' },
  { value: 'no', label: 'Нет' },
]
const RELATION_OPTIONS = [
  { value: '', label: 'Не помню' },
  { value: 'before', label: 'До' },
  { value: 'after', label: 'После' },
  { value: 'none', label: 'Не было' },
]
const SEX_OPTIONS = [
  { value: '', label: '—' },
  { value: 'M', label: 'Мужской' },
  { value: 'F', label: 'Женский' },
]

function Warnings({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null
  return (
    <span className="flex flex-col gap-0.5 text-warning" data-warning="">
      {messages.map((m) => (
        <span key={m}>{m}</span>
      ))}
    </span>
  )
}

/** Сообщения раздела (нормы, сегменты), которым нет своей строки. */
function sectionMessages(
  prefix: string,
  rows: string[],
  warnings: ParseWarning[],
  server: unknown,
): { warnings: string[]; error: string | null } {
  const orphan = (field: string | null | undefined) =>
    matchesPath(field, prefix) && !rows.some((row) => matchesPath(field, row))
  return {
    warnings: warnings.filter((w) => orphan(w.field)).map((w) => w.message),
    error: server instanceof ApiError && orphan(server.field) ? server.message : null,
  }
}

export function ScanForm({
  initial,
  warnings = [],
  notice,
  historyCount = 0,
  submitLabel = 'Сохранить',
  pending,
  error,
  onSubmit,
  onCancel,
  extraActions,
}: {
  initial: FormState
  warnings?: ParseWarning[]
  notice?: ReactNode
  historyCount?: number
  submitLabel?: string
  pending: boolean
  error: unknown
  onSubmit: (payload: ScanPayload) => void
  onCancel: () => void
  extraActions?: ReactNode
}) {
  const [state, setState] = useState<FormState>(initial)
  const [local, setLocal] = useState<Record<string, string>>({})
  const [timeError, setTimeError] = useState<unknown>(null)
  const suggestSeq = useRef(0)

  const set = (patch: Partial<FormState>) => setState((s) => ({ ...s, ...patch }))

  // Подсказанное время (R26): от тренировки дня, иначе 12:00. Меняется вместе с условиями.
  const suggestTime = async (day: string, relation: WorkoutRelation | null) => {
    if (!day) return
    const seq = ++suggestSeq.current
    try {
      const { measured_at } = await inbodyApi.suggestTime(day, relation)
      if (seq === suggestSeq.current) {
        setState((s) => ({ ...s, at: toLocalInput(new Date(measured_at)) }))
        setTimeError(null)
      }
    } catch (e) {
      if (seq === suggestSeq.current) setTimeError(e)
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const result = fromForm(state)
    if (!result.ok) {
      setLocal(result.errors)
      return
    }
    setLocal({})
    onSubmit(result.payload)
  }

  const err = (path: string) => errorAt(path, local, error)
  const hint = (path: string) => {
    const messages = warningsAt(path, warnings)
    return messages.length ? <Warnings messages={messages} /> : undefined
  }
  const numberInput = (key: ScanField | 'age' | 'height_cm', label: string, unit: string | null) => (
    <Input
      key={key}
      label={label}
      inputMode="decimal"
      suffix={unit ?? undefined}
      value={state.numbers[key]}
      onChange={(e) => set({ numbers: { ...state.numbers, [key]: e.target.value } })}
      error={err(key)}
      hint={hint(key)}
    />
  )

  const loose = looseWarnings(warnings)
  const normRows = NORM_KEYS.map((k) => `norms.${k}`)
  const normSection = sectionMessages('norms', normRows, warnings, error)
  const segmentRows = SEGMENT_KINDS.flatMap((kind) => SEGMENTS.map((s) => `segments.${kind}.${s}`))
  const segmentSection = sectionMessages('segments', segmentRows, warnings, error)

  return (
    <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
      {notice}
      {warnings.length > 0 ? (
        <div className="rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning" role="status">
          <p className="font-medium">Сверка нашла расхождения — проверь отмеченные поля.</p>
          {loose.length > 0 ? (
            <ul className="mt-1 list-disc pl-5">
              {loose.map((w) => (
                <li key={`${w.field}-${w.message}`}>{w.message}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      <Card title="Основное">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            label="Дата замера"
            type="date"
            value={state.day}
            onChange={(e) => {
              set({ day: e.target.value })
              void suggestTime(e.target.value, state.relation || null)
            }}
            error={err('day')}
            hint={hint('day')}
          />
          {FIELD_GROUPS[0]!.fields.map((f) => numberInput(f, FIELD_LABELS[f].label, FIELD_LABELS[f].unit))}
          {numberInput('age', 'Возраст', 'лет')}
          {numberInput('height_cm', 'Рост', 'см')}
          <Select
            label="Пол"
            value={state.sex}
            options={SEX_OPTIONS}
            onChange={(e) => set({ sex: e.target.value as FormState['sex'] })}
            error={err('sex')}
          />
        </div>
      </Card>

      {FIELD_GROUPS.slice(1).map((group) => (
        <Card key={group.id} title={group.title}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.fields.map((f) => numberInput(f, FIELD_LABELS[f].label, FIELD_LABELS[f].unit))}
          </div>
        </Card>
      ))}

      <Card title="Нормы" subtitle="Нижняя и верхняя граница каждой шкалы из отчёта">
        <div className="flex flex-col gap-2">
          {NORM_KEYS.map((key) => {
            const path = `norms.${key}`
            const rowError = err(path)
            const rowWarnings = warningsAt(path, warnings)
            const unit = FIELD_LABELS[key].unit ?? undefined
            const setNorm = (patch: Partial<{ low: string; high: string }>) =>
              set({ norms: { ...state.norms, [key]: { ...state.norms[key], ...patch } } })
            return (
              <div key={key} className="grid grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] items-start gap-2 sm:grid-cols-[12rem_8rem_8rem]">
                <span className="pt-2.5 text-sm text-fg">{NORM_LABELS[key]}</span>
                <Input
                  aria-label={`Норма «${NORM_LABELS[key]}»: от`}
                  inputMode="decimal"
                  placeholder="от"
                  suffix={unit}
                  value={state.norms[key].low}
                  onChange={(e) => setNorm({ low: e.target.value })}
                  aria-invalid={rowError ? true : undefined}
                />
                <Input
                  aria-label={`Норма «${NORM_LABELS[key]}»: до`}
                  inputMode="decimal"
                  placeholder="до"
                  suffix={unit}
                  value={state.norms[key].high}
                  onChange={(e) => setNorm({ high: e.target.value })}
                  aria-invalid={rowError ? true : undefined}
                />
                {rowError ? (
                  <p role="alert" className="col-span-3 -mt-1 text-sm text-danger">
                    {rowError}
                  </p>
                ) : null}
                {rowWarnings.length ? (
                  <p className="col-span-3 -mt-1 text-sm">
                    <Warnings messages={rowWarnings} />
                  </p>
                ) : null}
              </div>
            )
          })}
          {normSection.error ? <ErrorText>{normSection.error}</ErrorText> : null}
          {normSection.warnings.length ? (
            <p className="text-sm">
              <Warnings messages={normSection.warnings} />
            </p>
          ) : null}
        </div>
      </Card>

      <Card title="Сегменты" subtitle="Килограммы и процент от нормы по рукам, корпусу и ногам">
        <div className="grid gap-4 lg:grid-cols-2">
          {SEGMENT_KINDS.map((kind) => (
            <div key={kind} className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-muted">{SEGMENT_KIND_LABELS[kind]}</h3>
              {SEGMENTS.map((seg) => {
                const path = `segments.${kind}.${seg}`
                const rowError = err(path)
                const rowWarnings = warningsAt(path, warnings)
                const value = state.segments[kind][seg]
                const setSeg = (patch: Partial<{ kg: string; pct: string }>) =>
                  set({
                    segments: {
                      ...state.segments,
                      [kind]: { ...state.segments[kind], [seg]: { ...value, ...patch } },
                    },
                  })
                return (
                  <div key={seg} className="grid grid-cols-[minmax(0,1fr)_6.5rem_6.5rem] items-start gap-2">
                    <span className="pt-2.5 text-sm text-fg">{SEGMENT_LABELS[seg]}</span>
                    <Input
                      aria-label={`${SEGMENT_KIND_LABELS[kind]}: ${SEGMENT_LABELS[seg]}, кг`}
                      inputMode="decimal"
                      suffix="кг"
                      value={value.kg}
                      onChange={(e) => setSeg({ kg: e.target.value })}
                      aria-invalid={rowError ? true : undefined}
                    />
                    <Input
                      aria-label={`${SEGMENT_KIND_LABELS[kind]}: ${SEGMENT_LABELS[seg]}, % нормы`}
                      inputMode="decimal"
                      suffix="%"
                      value={value.pct}
                      onChange={(e) => setSeg({ pct: e.target.value })}
                      aria-invalid={rowError ? true : undefined}
                    />
                    {rowError ? (
                      <p role="alert" className="col-span-3 -mt-1 text-sm text-danger">
                        {rowError}
                      </p>
                    ) : null}
                    {rowWarnings.length ? (
                      <p className="col-span-3 -mt-1 text-sm">
                        <Warnings messages={rowWarnings} />
                      </p>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
        {segmentSection.error ? <ErrorText>{segmentSection.error}</ErrorText> : null}
        {segmentSection.warnings.length ? (
          <p className="mt-2 text-sm">
            <Warnings messages={segmentSection.warnings} />
          </p>
        ) : null}
      </Card>

      <Card title="Условия замера" subtitle="Время подставляется от тренировки этого дня; его можно поправить">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            label="Натощак"
            value={state.fasted}
            options={FASTED_OPTIONS}
            onChange={(e) => set({ fasted: e.target.value as FormState['fasted'] })}
            error={err('fasted')}
          />
          <Select
            label="Тренировка"
            value={state.relation}
            options={RELATION_OPTIONS}
            onChange={(e) => {
              const relation = e.target.value as FormState['relation']
              set({ relation })
              void suggestTime(state.day, relation || null)
            }}
            error={err('workout_relation')}
          />
          <Input
            label="Время замера"
            type="datetime-local"
            value={state.at}
            onChange={(e) => set({ at: e.target.value })}
            error={err('measured_at')}
            hint={timeError ? 'Не удалось подсказать время — укажи его сам или оставь пустым' : undefined}
          />
        </div>
        <Input
          containerClassName="mt-3"
          label="Заметка"
          maxLength={1000}
          value={state.note}
          onChange={(e) => set({ note: e.target.value })}
          error={err('note')}
        />
      </Card>

      {historyCount > 0 ? (
        <label className="flex min-h-11 items-center gap-3 rounded-lg border border-border bg-surface px-3 text-sm">
          <input
            type="checkbox"
            className="h-5 w-5 accent-[var(--accent)]"
            checked={state.importHistory}
            onChange={(e) => set({ importHistory: e.target.checked })}
          />
          <span>
            Подтяну из истории: {historyCount} {pluralRu(historyCount, ['прошлый замер', 'прошлых замера', 'прошлых замеров'])}
          </span>
        </label>
      ) : null}

      {Object.keys(local).length > 0 ? (
        <ErrorText>Проверь отмеченные поля</ErrorText>
      ) : (
        <ErrorText error={globalError(error)} />
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" loading={pending}>
          {submitLabel}
        </Button>
        <Button type="button" onClick={onCancel} disabled={pending}>
          Отменить
        </Button>
        {extraActions}
      </div>
    </form>
  )
}

/** Ошибка сервера, не привязанная ни к одному полю формы. */
function globalError(error: unknown): unknown {
  if (error instanceof ApiError && error.field && formPaths().some((p) => matchesPath(error.field, p))) return null
  return error
}
